<template>
  <Filters
    :initial-values
    :page-type="ROUTE_NAMES.CUSTOMER_CARE.ALL_REQUESTS"
    :status-filter-option="allRequestsStatusOptions"
    @apply-filters="handleApplyFilter"
  />

  <RevCard>
    <RevLoadingScreen
      v-if="isLoading"
      class="py-72"
      :text="i18n(translations.loadingScreenLabel)"
    />
    <div v-if="!isLoading">
      <NoRequestFoundBlock v-if="!hasRequests && !error">
        <p class="body-1">
          {{ i18n(translations.noRequestFoundDescription) }}
        </p>
        <RevLink
          to="https://backmarket.my.site.com/ssc/s/article/The-order-page-and-how-it-works"
        >
          <span class="body-1-bold">
            {{ i18n(translations.noRequestFoundLinkLabel) }}
          </span>
        </RevLink>
      </NoRequestFoundBlock>

      <div v-if="hasRequests && !error" class="space-y-16 py-16">
        <h2 class="text-heading-2 px-16">
          {{
            i18n(translations.requestsTotalCount, {
              count: payload?.metadata?.totalCount,
            })
          }}
        </h2>

        <AllRequestsTable :requests />

        <RevPagination
          class="py-24"
          :current-page
          :current-page-label="
            i18n(translations.currentPage, { page: currentPage })
          "
          :navigation-aria-label="i18n(translations.navigationLabel)"
          :next-aria-label="i18n(translations.nextPage)"
          :page-aria-label="
            (page) => i18n(translations.paginationLabel, { page })
          "
          :page-count
          :previous-aria-label="i18n(translations.previousPage)"
          @click="handlePagination"
        />
      </div>
      <TechnicalIssueBlock
        v-if="error"
        :title="i18n(translations.technicalIssueBlockTitle)"
        @try-again="refresh"
      />
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { navigateTo, useAsyncData, useRoute } from '#imports'
import { computed, ref } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { mapValues } from '@backmarket/utils/object/mapValues'
import { RevCard } from '@ds/components/Card'
import { RevLink } from '@ds/components/Link'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { RevPagination } from '@ds/components/Pagination'

import type { Filter } from '~/api/seller-after-sales/types/requests.types'
import { ROUTE_NAMES } from '~/constants/routes'
import { useFormattedQueryParams } from '~/scopes/customer-care/pages/customer-care/composables/useFormattedQueryParams'

import {
  EMPTY_FILTERS_VALUES,
  MULTISELECT_FILTERS_NAMES,
} from '../Filters/Filters.constants'
import filtersTranslations from '../Filters/Filters.translations'
import Filters from '../Filters/Filters.vue'
import NoRequestFoundBlock from '../NoRequestFoundBlock/NoRequestFoundBlock.vue'
import TechnicalIssueBlock from '../TechnicalIssueBlock/TechnicalIssueBlock.vue'

import translations from './AllRequests.translations'
import AllRequestsTable from './AllRequestsTable/AllRequestsTable.vue'

const i18n = useI18n()
const tracking = useTracking()
const route = useRoute()
const $httpFetch = useHttpFetch()

const DEFAULT_CURRENT_PAGE = 1
const REQUEST_COUNT_PER_PAGE = 20

const currentPage = ref(
  parseInt(route.query.page?.toString() || '', 10) || DEFAULT_CURRENT_PAGE,
)

const emit = defineEmits(['update-task-count'])

const formattedQueryParams = computed(() => {
  return mapValues(route.query, (value, key) =>
    MULTISELECT_FILTERS_NAMES.includes(key.toString())
      ? value?.toString().split(',')
      : value,
  )
})

const queryParams = computed(() => ({
  sort: 'UPDATED_AT',
  pageNumber: currentPage.value,
  pageSize: REQUEST_COUNT_PER_PAGE,
  ...formattedQueryParams.value,
}))

const {
  data: payload,
  status,
  error,
  refresh,
} = useAsyncData(
  () =>
    $httpFetch('/seller-after-sales/api/v1/requests', {
      query: queryParams.value,
    }),
  {
    lazy: true,
    watch: [queryParams],
  },
)

const isLoading = computed(() => status.value === 'pending')

emit('update-task-count')

const allRequestsStatusOptions = computed(() => {
  return [
    {
      label: i18n(filtersTranslations.statusFilterUpcomingOption),
      value: 'UPCOMING',
    },
    {
      label: i18n(filtersTranslations.statusFilterLateOption),
      value: 'LATE',
    },
    {
      label: i18n(filtersTranslations.statusFilterTodoOption),
      value: 'TODO',
    },
    {
      label: i18n(filtersTranslations.statusFilterNoActionNeededOption),
      value: 'NO_ACTION_NEEDED',
    },
    {
      label: i18n(filtersTranslations.statusFilterCompletedOption),
      value: 'COMPLETED',
    },
  ]
})

const hasRequests = computed(() => !isEmpty(payload?.value?.requests))
const requests = computed(() => payload.value?.requests || [])

const pageCount = computed(() =>
  Math.ceil(
    (payload?.value?.metadata?.totalCount ?? 0) / REQUEST_COUNT_PER_PAGE,
  ),
)

const initialValues = computed(() => {
  return { ...EMPTY_FILTERS_VALUES, ...formattedQueryParams.value }
})

async function handleApplyFilter(filters: Filter) {
  currentPage.value = DEFAULT_CURRENT_PAGE
  const nonEmptyQueryParams = ref(useFormattedQueryParams(filters))

  navigateTo({
    name: ROUTE_NAMES.CUSTOMER_CARE.ALL_REQUESTS,
    query: nonEmptyQueryParams.value,
  })
}

function handlePagination({ page }: { page: number }) {
  tracking.trackPaginationClicked({
    name: 'pagination_clicked',
    page_type: ROUTE_NAMES.CUSTOMER_CARE.ALL_REQUESTS,
    current_page: currentPage.value,
    target_page: page,
  })

  if (currentPage.value !== page) {
    currentPage.value = page

    navigateTo({
      name: ROUTE_NAMES.CUSTOMER_CARE.ALL_REQUESTS,
      query: {
        ...route.query,
        page: currentPage.value,
      },
    })
  }
}
</script>
